import { Button, Card, Form, FormProps, Space } from "antd";
import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import BackButtonIcon from "../../../../assets/icons/BackButtonIcon";
import SettingIcon from "../../../../assets/icons/SettingIcon";
import TopCard from "../../../../components/common/TopCard";
import EmployeeFileUpload from "../../../../components/Employees/EmployeeFileUpload";
import KPIAddMoreForm from "../../../../components/KIP/KPIAddMoreForm";
import KPIDetails from "../../../../components/KIP/KPIDetails";
import SubmitConfirmModal from "../../../../components/OwnAppraisals/SubmitConfirmModal";
import Loader from "../../../../components/ui/Loader";
import { useGetEmployeeDetailsQuery } from "../../../../features/employee/employeeApi";
import {
  useGetSingleKpiQuery,
  useSubmitOwnKpiMutation,
} from "../../../../features/performanceManagement/performnceManagementApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import { findPlanVal } from "../OpenKPISetup/OpenKPISetup";
import {
  planPeriodList,
  planTypeList,
} from "../OpenKPISetup/OpenKPISetupCreate";

const backUrl = "/admin/kpi-setup/own-kpi";

export default function OwnKPICreate() {
  const [form] = Form.useForm();
  const { kpiId } = useParams();
  const navigate = useNavigate();
  const { open, handleClose, handleOpen } = useModal<undefined, string>();
  const { authInfo } = useAppSelector((state) => state.auth);

  const departmentId = authInfo?.departmentId;
  const userId = String(authInfo?.id);

  const {
    isError: isEmployeeError,
    error: employeeError,
    data: employeeData,
    isLoading: isEmployeeLoading,
  } = useGetEmployeeDetailsQuery(String(userId), {
    skip: !userId,
  });
  const {
    isError: isOwnDetailsError,
    error: ownDetailsError,
    isLoading: isOwnDetailsLoading,
    data: ownDetailsData,
  } = useGetSingleKpiQuery(String(kpiId));

  const [submitOwnKpi, { isLoading, isError, isSuccess, error }] =
    useSubmitOwnKpiMutation();
  useError(isEmployeeError, employeeError, "Get employee data fail");
  useError(isOwnDetailsError, ownDetailsError, "Get own kPI fail");
  useError(isError, error, "kPI submit fail");
  const detailsData = employeeData?.data?.employee;

  const findDepartment = ownDetailsData?.data?.departments?.find(
    (item) => item?.departmentId === departmentId
  );

  const onFinish: FormProps<any>["onFinish"] = (values) => {
    const weighage = values?.kpiArray?.reduce(
      (acc: number, curr: any) => acc + Number(curr?.weightage),
      0
    );

    if (weighage !== 100) {
      toast("error", "Weighage must be 100%");
      return;
    }

    const newValues = {
      employeeId: authInfo?.id,
      designation: authInfo?.designationName,
      departmentId: authInfo?.departmentId,
      kpiId: kpiId,
      unit: "Hours",
      lineManagerId: detailsData?.manager_id,
      lineManagerName: detailsData?.manager?.member?.profile?.name,
      lineManagerDesignation: detailsData?.manager?.role?.name,
      positionStatus: "kpi",
      status: "pending",
      ...values,
    };

    submitOwnKpi(newValues);
  };

  // success toast message
  useEffect(() => {
    if (isSuccess) {
      toast("success", "KPI submitted successfully");
      handleClose();
      navigate("/admin/kpi-setup/own-kpi");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <>
      <SidebarLayout title="KPI Setup Report">
        <TopCard
          leftChildren={
            <>
              <Link to={backUrl}>
                <Button
                  className="normal-dark-button"
                  icon={<BackButtonIcon />}
                >
                  Back
                </Button>
              </Link>

              <div className="justify-start text-neutral-700 text-xl font-semibold">
                {ownDetailsData?.data?.kpi_title}
              </div>
            </>
          }
        >
          <Link to="">
            <Button
              type="primary"
              className="middle-button"
              icon={<SettingIcon />}
            />
          </Link>
        </TopCard>

        <div>
          <Card>
            {isEmployeeLoading || isOwnDetailsLoading ? (
              <Loader />
            ) : (
              <KPIDetails
                department={detailsData?.role?.department?.name}
                designation={detailsData?.role?.name}
                dueDate={
                  findDepartment?.due_date
                    ? formatDate(findDepartment?.due_date)
                    : null
                }
                employeeId={detailsData?.employee_id}
                employeeName={detailsData?.member?.profile?.name}
                lineManageDesignation={detailsData?.manager?.role?.name}
                lineManageId={detailsData?.manager?.employee_id}
                lineManageName={detailsData?.manager?.member?.profile?.name}
                planPeriod={findPlanVal(
                  planPeriodList,
                  String(ownDetailsData?.data?.plan_period)
                )}
                planType={findPlanVal(
                  planTypeList,
                  String(ownDetailsData?.data?.plan_type)
                )}
                unit="Hours"
              />
            )}

            <Form
              className="custom-form hide_star_mark"
              requiredMark="optional"
              layout="vertical"
              onFinish={onFinish}
              size="large"
              form={form}
            >
              <KPIAddMoreForm
                kpiArray="kpiArray"
                kpiType="kpiType"
                weighage="weightage"
                details="details"
              />

              <div>
                <div className="self-stretch px-3 py-1.5 bg-slate-100 rounded-md inline-flex justify-start items-center gap-2.5 w-full mb-4">
                  <div className="flex justify-start items-center gap-2.5">
                    <div className="justify-start text-slate-600 text-lg font-semibold">
                      Attachment
                    </div>
                  </div>
                </div>

                <div className="md:w-[75%]">
                  <Form.Item
                    name="attachment"
                    className="mb-4 md:w-1/2"
                    valuePropName="fileUrl"
                  >
                    <EmployeeFileUpload
                      form={form}
                      accept=".png, .jpg, .jpeg, .pdf"
                      name="attachment"
                      onChange={(url) =>
                        form.setFieldsValue({
                          attachment: url,
                        })
                      }
                      showImage
                    />
                  </Form.Item>
                </div>
              </div>

              <Space className="w-full mt-6" size={12}>
                {/* <Button
                  className="normal-dark-button modal-button"
                  size="middle"
                  icon={<DraftIcon />}
                >
                  Save as Draft
                </Button> */}

                <Button
                  className="modal-button"
                  type="primary"
                  size="middle"
                  onClick={() =>
                    handleOpen({
                      type: "CONFIRM",
                    })
                  }
                  disabled={isLoading}
                >
                  Submit
                </Button>
              </Space>

              {open.type === "CONFIRM" && (
                <SubmitConfirmModal
                  open={open.open}
                  handleClose={handleClose}
                  handleSubmit={() => form.submit()}
                  isLoading={isLoading}
                />
              )}
            </Form>
          </Card>
        </div>
      </SidebarLayout>
    </>
  );
}
